import React, { useState } from "react";
import { useGetSalesQuery } from "../services/api";
import { 
  FiDollarSign, 
  FiCalendar, 
  FiChevronDown, 
  FiChevronUp,
  FiShoppingBag,
  FiUser,
  FiCreditCard,
  FiPackage,
  FiTrendingUp,
  FiFilter,
  FiX,
  FiSearch,
  FiEye
} from "react-icons/fi";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(value || 0);
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-ET", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(dateString) {
  return new Date(dateString).toLocaleDateString("en-ET", { 
    month: "short", 
    day: "numeric", 
    year: "numeric", 
    hour: "2-digit", 
    minute: "2-digit" 
  });
}

export default function SalesPage() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [expandedSale, setExpandedSale] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  const { data, isLoading } = useGetSalesQuery({ start_date: startDate, end_date: endDate, payment_method: paymentMethod });
  const sales = data?.data || [];

  // Filter sales by search term and category
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.sale_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sale.customer_name && sale.customer_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // If category filter is active, check if any item in the sale matches
    const matchesCategory = !categoryFilter || (sale.items && sale.items.some(item => 
      item.material_type && item.material_type.toLowerCase() === categoryFilter.toLowerCase()
    ));
    
    return matchesSearch && matchesCategory;
  });

  const totalRevenue = filteredSales.reduce((sum, s) => sum + Number(s.total_amount), 0);
  
  // Calculate revenue by payment method
  const cashRevenue = filteredSales
    .filter(s => s.payment_method === 'cash')
    .reduce((sum, s) => sum + Number(s.total_amount), 0);
  
  const bankRevenue = filteredSales
    .filter(s => s.payment_method === 'bank_transfer')
    .reduce((sum, s) => sum + Number(s.total_amount), 0);
  
  const mobileRevenue = filteredSales
    .filter(s => s.payment_method === 'mobile_money')
    .reduce((sum, s) => sum + Number(s.total_amount), 0);
  
  // Calculate total grams sold
  const totalGramsSold = filteredSales.reduce((sum, sale) => {
    if (sale.items && Array.isArray(sale.items)) {
      return sum + sale.items.reduce((itemSum, item) => {
        return itemSum + Number(item.custom_quantity || 0);
      }, 0);
    }
    return sum;
  }, 0);

  // Calculate grams by material type (category)
  const gramsByCategory = filteredSales.reduce((acc, sale) => {
    if (sale.items && Array.isArray(sale.items)) {
      sale.items.forEach(item => {
        const materialType = item.material_type || 'Unknown';
        const grams = Number(item.custom_quantity || 0);
        acc[materialType] = (acc[materialType] || 0) + grams;
      });
    }
    return acc;
  }, {});

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setPaymentMethod("");
    setCategoryFilter("");
    setSearchTerm("");
  };

  const hasActiveFilters = startDate || endDate || paymentMethod || searchTerm || categoryFilter;

  // Get unique categories from all sales
  const availableCategories = [...new Set(
    sales.flatMap(sale => 
      (sale.items || []).map(item => item.material_type).filter(Boolean)
    )
  )].sort();

  return (
    <div style={{ 
      background: "#F8F9FA",
      minHeight: "100vh",
      padding: "2rem"
    }}>
      {/* Modern Page Header */}
      <div style={{ 
        marginBottom: "2rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "1rem"
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Sales History
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Here's what's happening with your sales today.
          </p>
        </div>

        {/* Search & Filter */}
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
          {/* Search Bar */}
          <div style={{ position: "relative", minWidth: "300px" }}>
            <FiSearch style={{
              position: "absolute",
              left: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94A3B8",
              fontSize: "1.1rem"
            }} />
            <input
              type="text"
              placeholder="Search by sale number or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: "2.75rem",
                paddingRight: "1rem",
                height: "44px",
                fontSize: "0.9rem",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                width: "100%",
                background: "white",
                outline: "none",
                transition: "all 0.2s"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3B82F6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E2E8F0";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.65rem 1.25rem",
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.9rem",
              fontWeight: 500,
              color: "#475569",
              cursor: "pointer",
              transition: "all 0.2s",
              position: "relative"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#3B82F6";
              e.currentTarget.style.background = "#F8FAFC";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#E2E8F0";
              e.currentTarget.style.background = "white";
            }}
          >
            <FiFilter />
            Filters
            {hasActiveFilters && (
              <span style={{
                position: "absolute",
                top: "-6px",
                right: "-6px",
                background: "#3B82F6",
                color: "white",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.7rem",
                fontWeight: 600,
                border: "2px solid white"
              }}>
                {[startDate, endDate, paymentMethod, categoryFilter].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          marginBottom: "2rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1.25rem"
          }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#1E293B", margin: 0 }}>
              Filter Sales
            </h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 1rem",
                  background: "transparent",
                  border: "1px solid #E2E8F0",
                  borderRadius: "6px",
                  fontSize: "0.85rem",
                  color: "#64748B",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#FEE2E2";
                  e.currentTarget.style.color = "#DC2626";
                  e.currentTarget.style.borderColor = "#FCA5A5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#64748B";
                  e.currentTarget.style.borderColor = "#E2E8F0";
                }}
              >
                <FiX size={16} />
                Clear All
              </button>
            )}
          </div>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", 
            gap: "1rem" 
          }}>
            <div>
              <label style={{ 
                display: "block",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#475569",
                marginBottom: "0.5rem"
              }}>
                Start Date
              </label>
              <input 
                type="date"
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.6rem 0.75rem",
                  border: "1px solid #E2E8F0",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  color: "#1E293B",
                  outline: "none",
                  transition: "all 0.2s"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3B82F6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: "block",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#475569",
                marginBottom: "0.5rem"
              }}>
                End Date
              </label>
              <input 
                type="date"
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.6rem 0.75rem",
                  border: "1px solid #E2E8F0",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  color: "#1E293B",
                  outline: "none",
                  transition: "all 0.2s"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3B82F6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: "block",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#475569",
                marginBottom: "0.5rem"
              }}>
                Payment Method
              </label>
              <select 
                value={paymentMethod} 
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.6rem 0.75rem",
                  border: "1px solid #E2E8F0",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  color: "#1E293B",
                  outline: "none",
                  background: "white",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3B82F6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="">All Methods</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>
            
            <div>
              <label style={{ 
                display: "block",
                fontSize: "0.85rem",
                fontWeight: 500,
                color: "#475569",
                marginBottom: "0.5rem"
              }}>
                Material Category
              </label>
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.6rem 0.75rem",
                  border: "1px solid #E2E8F0",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  color: "#1E293B",
                  outline: "none",
                  background: "white",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3B82F6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              >
                <option value="">All Categories</option>
                {availableCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Modern KPI Stats */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Sales */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              🛍️
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#10B981",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "0.25rem"
            }}>
              ↑ 12%
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Sales
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {filteredSales.length}
          </div>
        </div>

        {/* Total Revenue */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💰
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#10B981",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "0.25rem"
            }}>
              ↑ 12%
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Revenue
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(totalRevenue)}
          </div>
        </div>

        {/* Cash Sales */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💵
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#64748B",
              fontWeight: 600
            }}>
              {filteredSales.filter(s => s.payment_method === 'cash').length} txns
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Cash Sales
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(cashRevenue)}
          </div>
        </div>

        {/* Bank Transfer */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              🏦
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#64748B",
              fontWeight: 600
            }}>
              {filteredSales.filter(s => s.payment_method === 'bank_transfer').length} txns
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Bank Transfer
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(bankRevenue)}
          </div>
        </div>

        {/* Total Weight Sold */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #EC4899 0%, #DB2777 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ⚖️
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#10B981",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "0.25rem"
            }}>
              ↑ 20%
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Weight Sold
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {Number(totalGramsSold || 0).toFixed(2)}g
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(gramsByCategory).length > 0 && (
        <div style={{ marginBottom: "2rem" }}>
          <h2 style={{ 
            fontSize: "1.25rem", 
            fontWeight: 600, 
            marginBottom: "1.25rem",
            color: "#1E293B"
          }}>
            Sales by Category
          </h2>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "1.25rem"
          }}>
            {Object.entries(gramsByCategory)
              .sort(([, a], [, b]) => b - a)
              .map(([category, grams]) => {
                const percentage = totalGramsSold > 0 ? ((grams / totalGramsSold) * 100).toFixed(1) : '0.0';
                const categoryStyles = {
                  'Gold': { gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)', icon: '⚜️', progressBg: '#FEF3C7' },
                  'Silver': { gradient: 'linear-gradient(135deg, #E5E7EB 0%, #9CA3AF 100%)', icon: '🪙', progressBg: '#F3F4F6' },
                  'Diamond': { gradient: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)', icon: '💎', progressBg: '#DBEAFE' },
                  'Platinum': { gradient: 'linear-gradient(135deg, #A78BFA 0%, #8B5CF6 100%)', icon: '⭐', progressBg: '#EDE9FE' },
                };
                const style = categoryStyles[category] || { gradient: 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)', icon: '📦', progressBg: '#F1F5F9' };
                
                const itemCount = filteredSales.reduce((count, sale) => {
                  if (sale.items) {
                    return count + sale.items.filter(item => item.material_type === category).length;
                  }
                  return count;
                }, 0);
                
                return (
                  <div
                    key={category}
                    style={{
                      background: "white",
                      border: "1px solid #E2E8F0",
                      borderRadius: "12px",
                      padding: "1.5rem",
                      transition: "all 0.3s",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "none";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    {/* Progress bar at bottom */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: style.progressBg
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${percentage}%`,
                        background: style.gradient,
                        transition: 'width 0.6s ease-out'
                      }} />
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                      <div style={{ 
                        width: "48px",
                        height: "48px",
                        borderRadius: "10px",
                        background: style.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.25rem"
                      }}>
                        {style.icon}
                      </div>
                      <div style={{
                        fontSize: "0.8rem",
                        color: "#64748B",
                        fontWeight: 600,
                        background: style.progressBg,
                        padding: "4px 10px",
                        borderRadius: "6px"
                      }}>
                        {itemCount} items
                      </div>
                    </div>
                    
                    <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
                      {category}
                    </div>
                    <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B", marginBottom: "0.5rem" }}>
                      {grams.toFixed(2)}g
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#64748B" }}>
                      {percentage}% of total
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Sales List */}
      <div>
        {isLoading ? (
          <div style={{
            background: "white",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            padding: "4rem 2rem",
            textAlign: "center"
          }}>
            <div style={{
              width: "48px",
              height: "48px",
              border: "4px solid #E2E8F0",
              borderTopColor: "#3B82F6",
              borderRadius: "50%",
              margin: "0 auto 1rem",
              animation: "spin 1s linear infinite"
            }} />
            <div style={{ color: "#64748B", fontSize: "0.95rem" }}>Loading sales...</div>
          </div>
        ) : filteredSales.length === 0 ? (
          <div style={{
            background: "white",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            padding: "4rem 2rem",
            textAlign: "center"
          }}>
            <FiShoppingBag size={48} style={{ color: "#CBD5E1", marginBottom: "1rem" }} />
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "0.5rem" }}>
              No Sales Found
            </h3>
            <p style={{ color: "#64748B", marginBottom: "1.5rem" }}>
              {hasActiveFilters ? "Try adjusting your filters" : "No sales transactions yet"}
            </p>
            {hasActiveFilters && (
              <button 
                onClick={clearFilters}
                style={{
                  padding: "0.65rem 1.5rem",
                  background: "#3B82F6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#2563EB"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#3B82F6"}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gap: "1rem" }}>
            {filteredSales.map((sale, index) => (
              <SaleCard 
                key={sale.id} 
                sale={sale}
                expanded={expandedSale === sale.id}
                onToggle={() => setExpandedSale(expandedSale === sale.id ? null : sale.id)}
                index={index}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Modern Sale Card Component
function SaleCard({ sale, expanded, onToggle, index }) {
  const totalGrams = sale.items ? sale.items.reduce((sum, item) => sum + Number(item.custom_quantity || 0), 0) : 0;
  
  return (
    <div style={{
      background: "white",
      border: "1px solid #E2E8F0",
      borderRadius: "12px",
      overflow: "hidden",
      transition: "all 0.3s"
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
      e.currentTarget.style.borderColor = "#3B82F6";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = "none";
      e.currentTarget.style.borderColor = "#E2E8F0";
    }}
    >
      {/* Card Header */}
      <div style={{
        padding: "1.5rem",
        borderBottom: expanded ? "1px solid #E2E8F0" : "none",
        cursor: "pointer"
      }}
      onClick={onToggle}
      >
        <div style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          gap: "1.25rem",
          alignItems: "center"
        }}>
          {/* Status Icon */}
          <div style={{
            width: "56px",
            height: "56px",
            borderRadius: "12px",
            background: sale.payment_method === "cash" 
              ? "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
              : "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            boxShadow: sale.payment_method === "cash"
              ? "0 4px 12px rgba(245, 158, 11, 0.3)"
              : "0 4px 12px rgba(139, 92, 246, 0.3)"
          }}>
            {sale.payment_method === "cash" ? "💵" : "🏦"}
          </div>

          {/* Sale Info */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
              <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", margin: 0 }}>
                {sale.sale_number}
              </h3>
              <span style={{
                padding: "4px 10px",
                borderRadius: "6px",
                fontSize: "0.75rem",
                fontWeight: 600,
                background: sale.payment_method === "cash" ? "#FEF3C7" : "#EDE9FE",
                color: sale.payment_method === "cash" ? "#D97706" : "#7C3AED"
              }}>
                {sale.payment_method === "cash" ? "Cash" : "Bank Transfer"}
              </span>
            </div>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "1rem", 
              fontSize: "0.85rem", 
              color: "#64748B",
              flexWrap: "wrap"
            }}>
              <span style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <FiCalendar size={14} />
                {formatDateTime(sale.created_at)}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <FiUser size={14} />
                {sale.customer_name || "Walk-in"}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                <FiPackage size={14} />
                {sale.item_count} {sale.item_count === 1 ? "item" : "items"}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                ⚖️ {totalGrams.toFixed(2)}g
              </span>
            </div>
          </div>

          {/* Amount & Action */}
          <div style={{ textAlign: "right" }}>
            <div style={{ 
              fontSize: "1.5rem", 
              fontWeight: 700, 
              color: "#1E293B",
              marginBottom: "0.5rem" 
            }}>
              {formatCurrency(sale.total_amount)}
            </div>
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1rem",
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                fontSize: "0.85rem",
                color: "#475569",
                cursor: "pointer",
                transition: "all 0.2s",
                marginLeft: "auto"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#3B82F6";
                e.currentTarget.style.color = "white";
                e.currentTarget.style.borderColor = "#3B82F6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#F8FAFC";
                e.currentTarget.style.color = "#475569";
                e.currentTarget.style.borderColor = "#E2E8F0";
              }}
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            >
              <FiEye size={16} />
              {expanded ? "Hide" : "View"}
              {expanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && sale.items && (
        <div style={{
          background: "#F8FAFC",
          padding: "1.5rem"
        }}>
          {/* Items Header */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "1.25rem",
            paddingBottom: "1rem",
            borderBottom: "1px solid #E2E8F0"
          }}>
            <h4 style={{ 
              fontSize: "1rem", 
              fontWeight: 600,
              color: "#1E293B",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              margin: 0
            }}>
              <FiPackage style={{ color: "#3B82F6" }} />
              Sale Items
            </h4>
            <div style={{
              background: "#DBEAFE",
              padding: "0.5rem 0.75rem",
              borderRadius: "6px",
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#1E40AF"
            }}>
              Total: {totalGrams.toFixed(2)}g
            </div>
          </div>

          {/* Items Grid */}
          <div style={{ display: "grid", gap: "1rem", marginBottom: "1.5rem" }}>
            {sale.items.map((item, idx) => (
              <div key={idx} style={{
                background: "white",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                padding: "1.25rem",
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                gap: "1.25rem",
                alignItems: "center",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#3B82F6";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#E2E8F0";
                e.currentTarget.style.boxShadow = "none";
              }}
              >
                {/* Product Info */}
                <div>
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "0.5rem", 
                    marginBottom: "0.75rem",
                    flexWrap: "wrap"
                  }}>
                    <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#1E293B" }}>
                      {item.product_name}
                    </div>
                    {item.material_type && (
                      <span style={{
                        background: item.material_type === 'Gold' ? '#FEF3C7' : 
                                   item.material_type === 'Silver' ? '#F3F4F6' : 
                                   '#DBEAFE',
                        color: item.material_type === 'Gold' ? '#D97706' : 
                               item.material_type === 'Silver' ? '#6B7280' : 
                               '#1E40AF',
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem"
                      }}>
                        {item.material_type === 'Gold' ? '⚜️' : 
                         item.material_type === 'Silver' ? '🪙' : '💎'} {item.material_type}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", fontSize: "0.85rem", flexWrap: "wrap" }}>
                    <span style={{
                      background: "#DBEAFE",
                      color: "#1E40AF",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      {item.quantity} piece{item.quantity > 1 ? 's' : ''}
                    </span>
                    <span style={{
                      background: "#D1FAE5",
                      color: "#065F46",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px"
                    }}>
                      ⚖️ {Number(item.custom_quantity || 0).toFixed(2)} {item.custom_unit || 'g'}
                    </span>
                    {item.weight_grams && (
                      <span style={{
                        background: "#F1F5F9",
                        color: "#475569",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.75rem",
                        fontWeight: 600
                      }}>
                        {Number(item.weight_grams).toFixed(2)}g each
                      </span>
                    )}
                  </div>
                  {item.storage_note && (
                    <div style={{ 
                      fontSize: "0.8rem", 
                      color: "#64748B",
                      marginTop: "0.75rem",
                      padding: "0.5rem 0.75rem",
                      background: "#F8FAFC",
                      borderRadius: "6px",
                      border: "1px solid #E2E8F0"
                    }}>
                      📝 {item.storage_note}
                    </div>
                  )}
                </div>

                {/* Unit Price */}
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.75rem", color: "#94A3B8", marginBottom: "0.25rem" }}>
                    Unit Price
                  </div>
                  <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#475569" }}>
                    {formatCurrency(item.unit_price)}
                  </div>
                </div>

                {/* Total Price */}
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "0.75rem", color: "#94A3B8", marginBottom: "0.25rem" }}>
                    Total
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1E293B" }}>
                    {formatCurrency(item.unit_price * item.quantity)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sale Summary */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem",
            paddingTop: "1.25rem",
            borderTop: "1px solid #E2E8F0"
          }}>
            <div style={{
              background: "white",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid #E2E8F0"
            }}>
              <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem" }}>
                Cashier
              </div>
              <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#1E293B" }}>
                {sale.cashier_first} {sale.cashier_last}
              </div>
            </div>

            {sale.discount > 0 && (
              <div style={{
                background: "#D1FAE5",
                padding: "1rem",
                borderRadius: "8px",
                border: "1px solid #A7F3D0"
              }}>
                <div style={{ fontSize: "0.85rem", color: "#065F46", marginBottom: "0.5rem" }}>
                  Discount
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#065F46" }}>
                  -{formatCurrency(sale.discount)}
                </div>
              </div>
            )}

            <div style={{
              background: "linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid #93C5FD"
            }}>
              <div style={{ fontSize: "0.85rem", color: "#1E40AF", marginBottom: "0.5rem" }}>
                Final Total
              </div>
              <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1E40AF" }}>
                {formatCurrency(sale.total_amount)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
